<template>
  <transition v-bind="modalTransition">
    <div v-if="isOpen" class="fixed inset-0 z-9999 flex items-center justify-center bg-black bg-opacity-50">
      <div class="mx-auto w-full max-w-md bg-white p-6 shadow-4">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-xl font-semibold">
            {{ isEdit ? t('company_shifts.modal.edit_title') : t('company_shifts.modal.create_title') }}
          </h2>
          <button @click="cancel" class="hover:text-slate-400">
            <IcClose />
          </button>
        </div>

        <div v-if="isFetching" class="flex justify-center py-8">
          <svg class="h-8 w-8 animate-spin text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <Form v-else :validation-schema="companyShiftSchema" :initial-values="initialValues" @submit="onSubmit">
          <div class="space-y-4">
            <div>
              <label class="mb-2 block text-sm font-medium">{{ t('company_shifts.fields.name') }} *</label>
              <Field name="name" v-slot="{ field, errorMessage }">
                <input
                  v-bind="field"
                  type="text"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  :placeholder="t('company_shifts.placeholders.name')" />
                <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
              </Field>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="mb-2 block text-sm font-medium">{{ t('company_shifts.fields.start_time') }} *</label>
                <Field name="start_time" v-slot="{ field, errorMessage }">
                  <input
                    v-bind="field"
                    type="time"
                    class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                  <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
                </Field>
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium">{{ t('company_shifts.fields.end_time') }} *</label>
                <Field name="end_time" v-slot="{ field, errorMessage }">
                  <input
                    v-bind="field"
                    type="time"
                    class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                  <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
                </Field>
              </div>
            </div>

            <div>
              <label class="mb-2 block text-sm font-medium">
                {{ t('company_shifts.fields.groups') }}
              </label>
              <MultiSelect
                class="w-full"
                :options="groupOptions"
                @update:selectedValues="handleSelectedGroups" />
            </div>
          </div>

          <div class="mt-6 flex justify-end space-x-4">
            <button
              type="submit"
              :disabled="isLoading"
              class="bg-blue px-4 py-2 text-white shadow-sm hover:bg-blue-600 focus:outline-none disabled:opacity-50">
              <span v-if="isLoading">
                <ButtonLoader />
              </span>
              <span v-else>
                {{ t('globals.buttons.save') }}
              </span>
            </button>
            <button @click="cancel" type="button" class="text-slate-500 hover:text-slate-400" :disabled="isLoading">
              {{ t('globals.buttons.cancel') }}
            </button>
          </div>
        </Form>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { Form, Field } from 'vee-validate'
import { modalTransition } from '@/plugins/transitions'
import IcClose from '@/components/Icons/IcClose.vue'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import MultiSelect from '@/components/Forms/MultiSelect.vue'
import { useSuccessStore } from '@/store/success'
import { useCompanyStore } from '@/store/company'
import { useGroupStore } from '@/store/group'
import { companyShiftSchema } from '@/schemas/CompanyShiftSchema'
import ShiftResource, { ICreateShiftPayload, IUpdateShiftPayload } from '@/api/resources/shift'
import { formatOutsideRegularUtcTimeToLocalHHmm, localTimeInputToUtcHHmmssForApi } from '@/utils/date/date-time'
import { IShift } from '@/interfaces/entities/shifts'
import { useI18n } from '@/composable/useI18n'

interface ICompanyShiftFormValues {
  name: string
  start_time: string
  end_time: string
}

interface IMultiSelectOption {
  id: number
  value: number
  text: string
  selected: boolean
}

const props = defineProps<{
  isOpen: boolean
  shiftId?: number | null
}>()

const emit = defineEmits(['close', 'saved'])

const companyStore = useCompanyStore()
const groupStore = useGroupStore()
const { t } = useI18n()

const isLoading = ref(false)
const isFetching = ref(false)

const isEdit = computed(() => !!props.shiftId)

const initialValues = ref<ICompanyShiftFormValues>({
  name: '',
  start_time: '',
  end_time: '',
})
const selectedGroupIds = ref<number[]>([])

const groupOptions = computed<IMultiSelectOption[]>(() =>
  groupStore.groups.map((group) => ({
    id: group.id,
    value: group.id,
    text: group.name,
    selected: selectedGroupIds.value.includes(group.id),
  })),
)

watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      await groupStore.fetch()
      if (isEdit.value && props.shiftId) {
        await loadShift(props.shiftId)
      } else {
        initialValues.value = {
          name: '',
          start_time: '',
          end_time: '',
        }
        selectedGroupIds.value = []
      }
    }
  }
)

function formatTime(timeStr?: string) {
  if (!timeStr) return ''
  return formatOutsideRegularUtcTimeToLocalHHmm(timeStr) || timeStr
}

function resolveGroupIds(shift: IShift) {
  return shift.group_ids ?? shift.groups?.map((group) => group.id) ?? []
}

async function loadShift(id: number) {
  isFetching.value = true
  await nextTick()
  try {
    const shiftInStore = companyStore.shifts.find(s => s.id === id)
    
    if (shiftInStore) {
      initialValues.value = {
        name: shiftInStore.name,
        start_time: formatTime(shiftInStore.start_time),
        end_time: formatTime(shiftInStore.end_time),
      }
      selectedGroupIds.value = resolveGroupIds(shiftInStore)
    } else {
      const data = await ShiftResource.show(id)
      const shiftData: IShift = data.data || data
      
      initialValues.value = {
        name: shiftData.name,
        start_time: formatTime(shiftData.start_time),
        end_time: formatTime(shiftData.end_time),
      }
      selectedGroupIds.value = resolveGroupIds(shiftData)
    }
  } catch {
    // Erro ao buscar turno — feedback via interceptor
  } finally {
    isFetching.value = false
  }
}

function handleSelectedGroups(values: Array<number | string>) {
  selectedGroupIds.value = values.map(Number)
}

async function onSubmit(values: ICompanyShiftFormValues) {
  isLoading.value = true

  try {
    if (isEdit.value && props.shiftId) {
      const payload: IUpdateShiftPayload = {
        id: props.shiftId,
        name: values.name,
        start_time: localTimeInputToUtcHHmmssForApi(values.start_time),
        end_time: localTimeInputToUtcHHmmssForApi(values.end_time),
        group_ids: selectedGroupIds.value,
      }
      
      await ShiftResource.update(payload)
      useSuccessStore().setSuccess([t('company_shifts.success.updated')])
    } else {
      const payload: ICreateShiftPayload = {
        name: values.name,
        start_time: localTimeInputToUtcHHmmssForApi(values.start_time),
        end_time: localTimeInputToUtcHHmmssForApi(values.end_time),
        group_ids: selectedGroupIds.value,
      }
      
      await ShiftResource.create(payload)
      useSuccessStore().setSuccess([t('company_shifts.success.created')])
    }
    emit('saved')
    cancel()
  } catch {
    // Erro ao salvar turno — feedback via interceptor
  } finally {
    isLoading.value = false
  }
}

function cancel() {
  emit('close')
}
</script>
